import * as yup from 'yup';

// Email validation regex
const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;

// Login validation schema
export const loginSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .email('Please enter a valid email address'),
  password: yup
    .string()
    .required('Password is required')
    .min(6, 'Password must be at least 6 characters')
    .max(50, 'Password must not exceed 50 characters'),
  saveId: yup.boolean().default(false),
  keepSignedIn: yup.boolean().default(false),
});

// Registration validation schema
export const registerSchema = yup.object({
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must not exceed 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must not exceed 50 characters'),
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .email('Please enter a valid email address'),
  verificationCode: yup
    .string()
    .required('Verification code is required')
    .length(6, 'Verification code must be exactly 6 digits')
    .matches(/^\d{6}$/, 'Verification code must contain only numbers'),
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(50, 'Password must not exceed 26 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  phone: yup
    .string()
    .required('Phone number is required')
    .test('phone-format', 'Please enter a valid phone number', (value) => {
      if (!value) return false;
      return /^[+]?[\d\s\-()]+$/.test(value);
    }),
  address: yup
    .string()
    .nullable()
    .optional(),
  city: yup
    .string()
    .nullable()
    .optional(),
  state: yup
    .string()
    .nullable()
    .optional(),
  country: yup
    .string()
    .nullable()
    .optional(),
  zipCode: yup
    .string()
    .nullable()
    .optional(),
  birthMonth: yup
    .string()
    .nullable()
    .optional()
    .test('month-format', 'Please select a valid month', (value) => {
      if (!value) return true; // Allow empty/null
      return /^(0[1-9]|1[0-2])$/.test(value);
    }),
  birthDay: yup
    .string()
    .nullable()
    .optional()
    .test('day-format', 'Please select a valid day', (value) => {
      if (!value) return true; // Allow empty/null
      return /^(0[1-9]|[12]\d|3[01])$/.test(value);
    }),
  birthYear: yup
    .number()
    .nullable()
    .optional()
    .min(1900, 'Please enter a valid year')
    .max(new Date().getFullYear(), 'Please enter a valid year'),
  gender: yup
    .string()
    .nullable()
    .optional()
    .oneOf(['male', 'female', 'other'], 'Please select a valid gender'),
  referrer: yup
    .string()
    .nullable()
    .optional(),
  marketingConsent: yup.boolean().default(false),
  termsAccepted: yup
    .boolean()
    .oneOf([true], 'You must accept the terms and conditions')
    .required('You must accept the terms and conditions'),
  privacyPolicyAccepted: yup
    .boolean()
    .oneOf([true], 'You must accept the privacy policy')
    .required('You must accept the privacy policy'),
  dataCollectionConsent: yup.boolean().default(false),
  emailNotifications: yup.boolean().default(false),
  mobileAppPush: yup.boolean().default(false),
});

// Password reset validation schema
export const passwordResetSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .email('Please enter a valid email address'),
});

// New password validation schema
export const newPasswordSchema = yup.object({
  password: yup
    .string()
    .required('Password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(50, 'Password must not exceed 50 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
});

// Forgot password validation schema
export const forgotPasswordSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .email('Please enter a valid email address'),
});

// Verify OTP validation schema
export const verifyOtpSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .email('Please enter a valid email address'),
  otp: yup
    .string()
    .required('OTP is required')
    .length(6, 'OTP must be exactly 6 characters'),
});

// Recover password validation schema
export const recoverPasswordSchema = yup.object({
  email: yup
    .string()
    .required('Email is required')
    .matches(emailRegex, 'Please enter a valid email address')
    .email('Please enter a valid email address'),
  resetToken: yup
    .string()
    .required('Reset token is required'),
  newPassword: yup
    .string()
    .required('New password is required')
    .min(8, 'Password must be at least 8 characters')
    .max(50, 'Password must not exceed 50 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('newPassword')], 'Passwords must match'),
});

// TypeScript types for form data
export type LoginFormData = yup.InferType<typeof loginSchema>;
export type RegisterFormData = yup.InferType<typeof registerSchema>;
export type PasswordResetFormData = yup.InferType<typeof passwordResetSchema>;
export type NewPasswordFormData = yup.InferType<typeof newPasswordSchema>;
export type ForgotPasswordFormData = yup.InferType<typeof forgotPasswordSchema>;
export type VerifyOtpFormData = yup.InferType<typeof verifyOtpSchema>;
export type RecoverPasswordFormData = yup.InferType<typeof recoverPasswordSchema>; 